import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Receipt, CreditCard, Package, Banknote, CalendarDays, BadgeCheck,
  ExternalLink, ChevronLeft, ChevronRight, Clock, CalendarClock, Ban, Check,
} from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import BackButton from '../components/BackButton';
import PortalBillingMyPlan from '../components/portal/PortalBillingMyPlan';
import PortalAlaCarteQuotes from '../components/portal/PortalAlaCarteQuotes';
import {
  fetchProgramDeliverablesBundle,
  resolveAccountLifecycle,
  getMinimumTermOption,
} from '../utils/accountProgramDeliverables';
import { submitPortalFulfillmentRequest } from '../services/portalFulfillmentRequestService';
import { doc, getDoc, collection, query, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../firebase';
import PortalBackground from '../components/PortalBackground';
import { resolveClientLifecycle } from '../utils/accountLifecycle'; // Active / Canceling / ended

const statusBadgeClass = (status) => {
  const s = (status || '').toString().toLowerCase();
  if (['paid', 'active', 'succeeded', 'complete', 'completed'].includes(s)) {
    return 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300';
  }
  if (['overdue', 'failed', 'past_due', 'uncollectible'].includes(s)) {
    return 'bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-300';
  }
  if (['void', 'canceled', 'cancelled', 'inactive'].includes(s)) {
    return 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300';
  }
  return 'bg-amber-50 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300';
};

const StatusBadge = ({ status }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold whitespace-nowrap ${statusBadgeClass(status)}`}>
    {status}
  </span>
);

const BillingPage = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();

  const [invoiceHistory, setInvoiceHistory] = useState([]);
  const [subscriptionDetails, setSubscriptionDetails] = useState({
    plan: '',
    amount: '',
    nextBillingDate: '',
  });
  const [clientData, setClientData] = useState(null);
  const [linkedAccountId, setLinkedAccountId] = useState(null);
  const [programItems, setProgramItems] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [planLoading, setPlanLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [cancelRequestSent, setCancelRequestSent] = useState(false);
  const itemsPerPage = 10;

  const lifecycle = useMemo(
    () => (clientData ? resolveAccountLifecycle(clientData, null) : null),
    [clientData]
  );

  /** Same day-inclusive rule as plan reductions: unknown term end ⇒ not past term. */
  const pastMinimumTerm = useMemo(() => {
    const end = lifecycle?.termEndDate;
    if (!(end instanceof Date) || Number.isNaN(end.getTime())) return false;
    const e = new Date(end);
    e.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return today.getTime() >= e.getTime();
  }, [lifecycle]);

  const cancelScheduled = lifecycle?.cancelType === 'cancels';

  const formatLifecycleDate = (d) =>
    d instanceof Date && !Number.isNaN(d.getTime()) ? d.toLocaleDateString() : null;

  const minimumTermLabel = useMemo(() => {
    const prog = typeof clientData?.program === 'object' ? clientData.program : null;
    const opt = getMinimumTermOption(prog?.minimumTerm);
    if (opt) return opt.label;
    const contract = clientData?.signedContract || clientData?.contractData;
    const months = parseInt(contract?.contractLength, 10);
    if (months && !Number.isNaN(months)) {
      if (months === 12) return '1 Year';
      return `${months} Month${months === 1 ? '' : 's'}`;
    }
    return null;
  }, [clientData]);

  const loadUserBillingData = useCallback(async () => {
    if (!currentUser) return;

    try {
      setLoading(true);

      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
      let accountId = null;
      if (userDoc.exists()) {
        const userData = userDoc.data();
        accountId = userData.accountId;
      }

      if (accountId) {
        setLinkedAccountId(accountId);
        const accountDoc = await getDoc(doc(db, 'accounts', accountId));
        if (accountDoc.exists()) {
          const accountData = accountDoc.data();
          setClientData(accountData);

          if (accountData.program) {
            setSubscriptionDetails({
              plan: accountData.program.status || '',
              amount: accountData.program.monthlyAmount
                ? `$${accountData.program.monthlyAmount.toLocaleString()}`
                : '',
              nextBillingDate: accountData.program.billingCycle || '',
            });
          }

          setPlanLoading(true);
          try {
            const bundle = await fetchProgramDeliverablesBundle(accountData, null);
            setProgramItems(bundle.programItems || []);
            setCatalogItems(bundle.itemsData || []);
          } catch (e) {
            console.error('Billing: load plan bundle', e);
            setProgramItems([]);
            setCatalogItems([]);
          } finally {
            setPlanLoading(false);
          }

          try {
            const payments = [];

            try {
              const paymentsQuery = query(
                collection(db, 'accounts', accountId, 'payments'),
                orderBy('createdAt', 'desc')
              );
              const paymentsSnapshot = await getDocs(paymentsQuery);
              payments.push(
                ...paymentsSnapshot.docs.map((d) => ({
                  id: d.id,
                  ...d.data(),
                }))
              );
            } catch (err) {
              console.error('Error loading Stripe payments for invoice history:', err);
            }

            if (accountData.billing?.payments) {
              const manualPayments = (accountData.billing.payments || []).filter((p) => !p.stripeInvoiceId);
              const dedupedManual = manualPayments.filter(
                (p) =>
                  !payments.some(
                    (sp) =>
                      (sp.id && p.id && sp.id === p.id) ||
                      (sp.stripeInvoiceId &&
                        p.stripeInvoiceId &&
                        sp.stripeInvoiceId === p.stripeInvoiceId)
                  )
              );
              payments.push(...dedupedManual);
            }

            const seenStripeIds = new Set();
            const deduped = payments.filter((p) => {
              const sid = p.stripeInvoiceId || null;
              if (sid) {
                if (seenStripeIds.has(sid)) return false;
                seenStripeIds.add(sid);
              }
              return true;
            });

            const sorted = deduped.sort((a, b) => {
              const dateA = a.createdAt?.toDate?.() || a.createdAt || a.dueDate;
              const dateB = b.createdAt?.toDate?.() || b.createdAt || b.dueDate;
              return new Date(dateB || 0) - new Date(dateA || 0);
            });

            const formattedInvoices = sorted.map((payment) => {
              const created =
                payment.createdAt?.toDate?.() ||
                (payment.createdAt ? new Date(payment.createdAt) : null);
              const due = payment.dueDate ? new Date(payment.dueDate) : null;
              const date = created || due;
              const rawStatus = (payment.status || 'pending').toString();
              const normalizedStatus = rawStatus.toLowerCase() === 'paid' ? 'Paid' : rawStatus;

              return {
                id: payment.id || payment.stripeInvoiceId || `${Date.now()}-${Math.random()}`,
                date: date ? date.toLocaleDateString() : 'Not set',
                invoiceNumber: payment.stripeInvoiceId || payment.id || 'N/A',
                amount: `$${(payment.amount ?? 0).toLocaleString()}`,
                status: normalizedStatus,
                hostedInvoiceUrl: payment.hostedInvoiceUrl || null,
                receiptUrl: payment.receiptUrl || null,
              };
            });

            setInvoiceHistory(formattedInvoices);
          } catch (err) {
            console.error('Error building invoice history from payments:', err);
          }
        }
      } else {
        setLinkedAccountId(null);
        setClientData(null);
        setProgramItems([]);
        setCatalogItems([]);
      }

      if (!accountId) {
        const subscriptionDoc = await getDoc(doc(db, 'users', currentUser.uid, 'billing', 'subscription'));
        if (subscriptionDoc.exists()) {
          setSubscriptionDetails(subscriptionDoc.data());
        }

        const invoicesQuery = query(collection(db, 'users', currentUser.uid, 'invoices'), orderBy('date', 'desc'));
        const invoicesSnapshot = await getDocs(invoicesQuery);
        const invoices = [];
        invoicesSnapshot.forEach((d) => {
          invoices.push({ id: d.id, ...d.data() });
        });
        setInvoiceHistory(invoices);
      }
    } catch (error) {
      console.error('Error loading billing data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser]);

  useEffect(() => {
    if (currentUser) {
      loadUserBillingData();
    }
  }, [currentUser, loadUserBillingData]);

  useEffect(() => {
    setCancelRequestSent(false);
    setCancelReason('');
    setCancelModalOpen(false);
  }, [linkedAccountId]);

  const totalPages = Math.ceil(invoiceHistory.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentInvoices = invoiceHistory.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage((prev) => Math.min(prev + 1, totalPages));
  };

  const handleCancelRequest = async () => {
    if (!currentUser?.uid || !linkedAccountId || cancelRequestSent || !pastMinimumTerm) return;
    setCancelSubmitting(true);
    try {
      let requestedBy = currentUser.displayName || currentUser.email || '';
      let company = '';
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          const ud = userDoc.data();
          const full = `${ud?.firstName || ''} ${ud?.lastName || ''}`.trim();
          if (full) requestedBy = full;
          const c = ud?.company;
          if (c && c !== 'Unknown Company') company = c;
        }
      } catch (_) {}

      const prog = typeof clientData?.program === 'object' ? clientData.program : null;
      await submitPortalFulfillmentRequest({
        kind: 'program',
        source: 'billing',
        details: {
          kind: 'cancellation_request',
          programName:
            (typeof clientData?.program === 'string' ? clientData.program : prog?.status) || 'Program',
          billingCycle: prog?.billingCycle || clientData?.billingCycle || 'monthly',
          monthlyAmount: prog?.monthlyAmount ?? clientData?.billing?.monthlyAmount ?? null,
          minimumTerm: minimumTermLabel,
          termEndDate: lifecycle?.termEndDate ? lifecycle.termEndDate.toISOString() : null,
          nextBillingDate: lifecycle?.renewsOn ? lifecycle.renewsOn.toISOString() : null,
          pastMinimumTerm: true,
          reason: cancelReason.trim() || null,
        },
        userId: currentUser.uid,
        userEmail: currentUser.email || '',
        accountId: linkedAccountId,
        requestedBy,
        company,
      });
      setCancelRequestSent(true);
      setCancelModalOpen(false);
      window.alert('Your cancellation request has been sent. Our team will follow up to confirm.');
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'Could not send cancellation request.');
    } finally {
      setCancelSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="relative min-h-screen text-gray-900 dark:text-white">
        <PortalBackground />
        <div className="relative max-w-5xl mx-auto p-6">
          {/* Header skeleton */}
          <div className="flex justify-center mb-6">
            <div className="h-8 w-56 rounded bg-gray-100 dark:bg-gray-800 animate-pulse"></div>
          </div>
          {/* Summary card skeletons */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-2.5 w-1/2 rounded bg-gray-100 dark:bg-gray-800"></div>
                  <div className="h-4 w-2/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                </div>
              </div>
            ))}
          </div>
          {/* Invoice row skeletons */}
          <div className="space-y-2">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4 animate-pulse flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-gray-100 dark:bg-gray-800 shrink-0"></div>
                <div className="flex-1 space-y-2">
                  <div className="h-3 w-1/3 rounded bg-gray-100 dark:bg-gray-800"></div>
                  <div className="h-2.5 w-1/4 rounded bg-gray-100 dark:bg-gray-800"></div>
                </div>
                <div className="h-4 w-16 rounded-full bg-gray-100 dark:bg-gray-800 shrink-0"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const statusDisplay = (() => {
    if (!clientData?.program) return 'Inactive';
    const state = resolveClientLifecycle(clientData).state;
    if (state === 'Active') return 'Active';
    if (state === 'Canceling') return 'Canceling';
    return 'Inactive';
  })();

  const summaryCards = clientData?.program
    ? [
        {
          label: 'Program',
          value:
            typeof clientData.program === 'string'
              ? clientData.program
              : clientData.program.status || 'Not Started',
          icon: Package,
          tile: 'bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400',
        },
        {
          label: 'Monthly Amount',
          value: (() => {
            const prog = typeof clientData.program === 'object' ? clientData.program : null;
            const amt =
              prog?.monthlyAmount != null
                ? Number(prog.monthlyAmount)
                : clientData.billing?.monthlyAmount != null
                  ? Number(clientData.billing.monthlyAmount)
                  : null;
            return amt != null && !Number.isNaN(amt) ? `$${amt.toLocaleString()}` : '$0';
          })(),
          icon: Banknote,
          tile: 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
        },
        {
          label: 'Billing Cycle',
          value:
            (typeof clientData.program === 'object'
              ? clientData.program?.billingCycle
              : null) || clientData.billingCycle || 'Monthly',
          icon: CalendarDays,
          tile: 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400',
        },
        {
          label: 'Status',
          value: statusDisplay,
          icon: BadgeCheck,
          tile: 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
        },
        {
          label: 'Minimum Term',
          value: minimumTermLabel || '—',
          sub: (() => {
            const endStr = formatLifecycleDate(lifecycle?.termEndDate);
            if (!endStr) return null;
            return pastMinimumTerm ? `Completed ${endStr}` : `Ends ${endStr}`;
          })(),
          icon: Clock,
          tile: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
        },
        {
          label: cancelScheduled ? 'Cancels On' : 'Next Billing',
          value:
            (cancelScheduled
              ? formatLifecycleDate(lifecycle?.cancelEffectiveDate) ||
                formatLifecycleDate(lifecycle?.renewsOn)
              : formatLifecycleDate(lifecycle?.renewsOn)) || '—',
          icon: CalendarClock,
          tile: 'bg-sky-50 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400',
        },
      ]
    : [];

  return (
    <div className="relative min-h-screen text-gray-900 dark:text-white">
      <PortalBackground />
      <div className="relative max-w-5xl mx-auto p-6">
        {/* Header */}
        <div className="relative mb-6">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 z-10">
            <BackButton />
          </div>
          <div className="text-center px-14 md:px-32 py-1">
            <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">Billing & Invoices</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Your plan, payments, and invoice history in one place.
            </p>
          </div>
        </div>

        {/* My Plan summary cards */}
        {clientData?.program && (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
            {summaryCards.map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.label}
                  className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4"
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${card.tile}`}>
                      <Icon className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-medium text-gray-500 dark:text-gray-400 truncate">{card.label}</p>
                      <p className="text-lg font-bold text-gray-900 dark:text-white truncate" title={`${card.value}`}>
                        {card.value}
                      </p>
                      {card.sub && (
                        <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{card.sub}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Cancellation — unlocked once the minimum term has passed */}
        {clientData?.program && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 mb-8">
            {cancelScheduled ? (
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <Ban className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    Your subscription is scheduled to cancel
                    {formatLifecycleDate(lifecycle?.cancelEffectiveDate) ||
                    formatLifecycleDate(lifecycle?.renewsOn)
                      ? ` on ${
                          formatLifecycleDate(lifecycle?.cancelEffectiveDate) ||
                          formatLifecycleDate(lifecycle?.renewsOn)
                        }`
                      : ''}
                    .
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    Contact our team if you'd like to keep your plan active.
                  </p>
                </div>
              </div>
            ) : pastMinimumTerm ? (
              <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                <div className="flex items-center gap-4 flex-1 min-w-0">
                  <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                    <BadgeCheck className="w-4 h-4" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 dark:text-white">
                      Your minimum term is complete.
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      You can request to cancel your subscription at any time. Your plan stays active
                      until your team confirms the change.
                    </p>
                  </div>
                </div>
                {cancelRequestSent ? (
                  <span className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-green-600 text-white shrink-0">
                    <Check className="w-4 h-4" strokeWidth={3} />
                    Request sent
                  </span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCancelModalOpen(true)}
                    className="shrink-0 px-4 py-2 rounded-lg text-sm font-semibold border-2 border-red-600 text-red-600 dark:text-red-400 dark:border-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    Cancel Subscription
                  </button>
                )}
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white">
                    {lifecycle?.termEndDate
                      ? `Your minimum term runs through ${formatLifecycleDate(lifecycle.termEndDate)}.`
                      : 'Your plan includes a minimum term.'}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                    {lifecycle?.termEndDate
                      ? 'Cancellation becomes available after that date.'
                      : 'Contact our team with any questions about your term or cancellation options.'}
                  </p>
                </div>
              </div>
            )}
          </div>
        )}

        {!clientData?.program && subscriptionDetails.plan && (
          <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-5 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                <CreditCard className="w-4 h-4" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">{subscriptionDetails.plan}</h3>
                <p className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{subscriptionDetails.amount}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Next billing date: {subscriptionDetails.nextBillingDate}
                </p>
              </div>
            </div>
          </div>
        )}

        {linkedAccountId && (
          <PortalAlaCarteQuotes accountId={linkedAccountId} currentUser={currentUser} />
        )}

        {/* Invoice History — near top */}
        <div className="mb-10">
          <h2 className="text-base font-semibold text-gray-900 dark:text-white flex items-center justify-center gap-2 text-center mb-4">
            <Receipt className="w-4 h-4 text-red-600" />
            Invoice History
          </h2>
          {invoiceHistory.length > 0 ? (
            <>
              <div className="space-y-2">
                {currentInvoices.map((invoice) => (
                  <div
                    key={invoice.id}
                    className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm p-4 flex items-center gap-3"
                  >
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0 bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                      <Receipt className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {invoice.hostedInvoiceUrl ? (
                        <a
                          href={invoice.hostedInvoiceUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600 dark:text-red-400 hover:underline max-w-full"
                          title="View invoice"
                        >
                          <span className="truncate">{invoice.invoiceNumber}</span>
                          <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                        </a>
                      ) : (
                        <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {invoice.invoiceNumber}
                        </p>
                      )}
                      <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                        <CalendarDays className="w-3 h-3 shrink-0" />
                        <span className="whitespace-nowrap">{invoice.date}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-sm font-semibold text-gray-900 dark:text-gray-100 whitespace-nowrap">
                        {invoice.amount}
                      </span>
                      <StatusBadge status={invoice.status} />
                    </div>
                  </div>
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-3 flex items-center justify-between px-1">
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    Showing {startIndex + 1} to {Math.min(endIndex, invoiceHistory.length)} of {invoiceHistory.length}{' '}
                    invoices
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === 1
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </button>
                    <span className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-lg transition-colors ${
                        currentPage === totalPages
                          ? 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                          : 'text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700'
                      }`}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
              <Receipt className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No invoices found</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                Your invoice history will appear here once you have billing activity.
              </p>
            </div>
          )}
        </div>

        {linkedAccountId && clientData ? (
          <PortalBillingMyPlan
            darkMode={darkMode}
            currentUser={currentUser}
            accountId={linkedAccountId}
            account={clientData}
            catalogItems={catalogItems}
            programItems={programItems}
            planLoading={planLoading}
            onReload={loadUserBillingData}
          />
        ) : (
          !subscriptionDetails.plan &&
          !clientData?.program && (
            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm flex flex-col items-center justify-center py-14 px-6 text-center">
              <CreditCard className="w-10 h-10 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm font-medium text-gray-600 dark:text-gray-300">No linked client account</p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 max-w-sm">
                Link your account to see program details and manage deliverables here.
              </p>
            </div>
          )
        )}
      </div>

      {cancelModalOpen && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
          onClick={() => !cancelSubmitting && setCancelModalOpen(false)}
          role="presentation"
        >
          <div
            className={`bg-white dark:bg-gray-900 rounded-2xl shadow-xl border border-gray-200 dark:border-gray-800 w-full max-w-md overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="cancel-subscription-title"
          >
            <div className="bg-red-600 p-4">
              <h3 id="cancel-subscription-title" className="text-lg font-semibold text-white text-center">
                Cancel Subscription
              </h3>
            </div>
            <div className="p-6">
              <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                This sends a cancellation request to our team. Your plan stays active until the
                cancellation is confirmed
                {formatLifecycleDate(lifecycle?.renewsOn)
                  ? `, and your next billing date is ${formatLifecycleDate(lifecycle?.renewsOn)}`
                  : ''}
                .
              </p>
              <label
                className={`block text-sm font-medium mt-4 mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
                htmlFor="cancel-reason"
              >
                Anything we should know? (optional)
              </label>
              <textarea
                id="cancel-reason"
                rows={3}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                placeholder="Tell us why you're cancelling…"
                className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-red-500/50 ${
                  darkMode
                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
                }`}
              />
              <div className="flex justify-end gap-3 mt-5">
                <button
                  type="button"
                  disabled={cancelSubmitting}
                  onClick={() => setCancelModalOpen(false)}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    darkMode
                      ? 'bg-gray-700 text-white hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                  }`}
                >
                  Keep My Plan
                </button>
                <button
                  type="button"
                  disabled={cancelSubmitting}
                  onClick={handleCancelRequest}
                  className={`px-4 py-2 rounded-lg text-sm font-semibold text-white transition-colors ${
                    cancelSubmitting ? 'bg-gray-400 cursor-not-allowed' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {cancelSubmitting ? 'Sending…' : 'Submit Cancellation Request'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BillingPage;
